import React, { useState, useEffect, useCallback } from "react";
import { Mail, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { exportAnalysisPdf } from "@/lib/pdf";
import { api } from "@/lib/apiClient";

// 1-tap "Email to me" — builds the same branded PDF and mails it to the signed-in coach's own address.
export const EmailReportButton = ({ targetRef, kind, title, subtitle, meta = [], label, className = "", testid = "email-report-btn" }) => {
  const [busy, setBusy] = useState(false);
  const run = useCallback(async () => {
    const node = targetRef?.current;
    if (!node) { toast.error("Nothing to email yet"); return { ok: false, error: "No report on this screen yet" }; }
    setBusy(true);
    try {
      const pdf_base64 = await exportAnalysisPdf({ node, title, subtitle, meta, output: "base64" });
      const r = await api.post("/report/email", { kind, pdf_base64, label: label || subtitle || title }, { timeout: 90000 });
      toast.success(`Emailed to ${r.data.to}`);
      return { ok: true, to: r.data.to, title: r.data.title };
    } catch (e) {
      const msg = e?.response?.data?.detail || "Could not email the report";
      toast.error(msg);
      return { ok: false, error: msg };
    } finally { setBusy(false); }
  }, [targetRef, kind, title, subtitle, meta, label]);

  // Voice: "email me this report" → octon:email-report (detail.resolve gets the result)
  useEffect(() => {
    const h = async (e) => { const res = await run(); e?.detail?.resolve?.(res); };
    window.addEventListener("octon:email-report", h);
    return () => window.removeEventListener("octon:email-report", h);
  }, [run]);

  return (
    <button onClick={run} disabled={busy} data-testid={testid} aria-label="Email this report to me"
      className={`inline-flex items-center gap-2 rounded-full border border-cyan-400/40 bg-cyan-400/10 px-4 py-2 text-xs font-semibold text-cyan-200 transition hover:bg-cyan-400/20 hover:text-white disabled:opacity-50 ${className}`}>
      {busy ? <Loader2 className="animate-spin" size={14} /> : <Mail size={14} />} {busy ? "Sending…" : "Email to me"}
    </button>
  );
};
